import { observer } from 'mobx-react';
import { Fragment, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
    Button,
    Card,
    Chip,
    Flex,
    NumberField,
    Stepper,
    Text,
    Textarea,
    TextField,
    toast,
} from '@servicetitan/anvil2';
import IconAdd from '@servicetitan/anvil2/assets/icons/material/round/add.svg';
import IconAttachFile from '@servicetitan/anvil2/assets/icons/material/round/attach_file.svg';
import IconClose from '@servicetitan/anvil2/assets/icons/material/round/close.svg';
import IconDelete from '@servicetitan/anvil2/assets/icons/material/round/delete.svg';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { File } from '@servicetitan/web-feature-file-service';
import { DateField, Page, StickyFooter } from '@servicetitan/web-feature-ui';

import {
    IMobileMaintenanceServiceEntryDetailsVm,
    MobileVehicleApi,
} from '@fleet-pro/mobile-webview-api';

import { NotFoundIllustration } from '../../common/components/not-found';
import { SingleSelect, SingleSelectOption } from '../../common/components/single-select';
import { RequestState } from '../../common/utils/request-states';
import { ServiceEntryDataStore } from '../stores/service-entry-data.store';
import { ServiceEntryViewStore } from '../stores/service-entry-view.store';
import { formatDate } from '../utils/format-date';
import { getFileName } from '../utils/get-file-name';
import * as styles from './create-service-entry.module.css';

interface LocationState {
    serviceEntry?: IMobileMaintenanceServiceEntryDetailsVm;
}

export const CreateServiceEntryPage = provide({
    singletons: [MobileVehicleApi, ServiceEntryDataStore, ServiceEntryViewStore],
})(
    observer(() => {
        const [
            {
                fetchServiceTypes,
                cancelFetchServiceTypes,
                fetchVehiclesByVinPrefix,
                fetchVehiclesByVinPrefixRequestState,
            },
        ] = useDependencies(ServiceEntryDataStore);
        const [
            {
                serviceEntryForm,
                serviceTypeOptions,
                categoryOptions,
                vinOptions,
                selectedVehicleDisplayName,
                isEditMode,
                isSubmitting,
                hasFileValidationError,
                initEditMode,
                resetForm,
                handleVinChange,
                handleSubmit,
                addServiceRecord,
                removeServiceRecord,
                handleFileChange,
                handleFileDelete,
                isStepValid,
            },
        ] = useDependencies(ServiceEntryViewStore);

        const [step, setStep] = useState<string>('service-details');
        const history = useHistory();
        const location = useLocation<LocationState>();

        // Initialize edit mode if service entry data is passed via location state
        useEffect(() => {
            if (location.state?.serviceEntry) {
                initEditMode(location.state.serviceEntry);
            }

            return () => {
                resetForm();
            };
        }, [location.state?.serviceEntry, initEditMode, resetForm]);

        useEffect(() => {
            fetchServiceTypes();

            return () => {
                cancelFetchServiceTypes();
            };
        }, [fetchServiceTypes, cancelFetchServiceTypes]);

        const handleComplete = async () => {
            const success = await handleSubmit();

            if (success) {
                history.replace('/service-entries');

                toast.success({
                    title: isEditMode ? 'Service Entry Updated' : 'Service Entry Added',
                    message: isEditMode
                        ? 'Service Entry has been updated successfully'
                        : 'Service Entry has been added successfully',
                });
            }
        };

        const handleAddFileClick = async () => {
            try {
                const result = await File.pick({
                    type: 'any',
                    multiple: true,
                });

                if (result?.files && result.files.length > 0) {
                    handleFileChange(result.files);
                }
            } catch (error) {
                toast.danger({
                    title: 'File Selection Failed',
                    message: 'Unable to select files. Please try again.',
                });
            }
        };

        // Get selected VIN option for the SingleSelect
        // id = vehicleName, label = vin (matching vinOptions structure)
        const selectedVinOption: SingleSelectOption | null = serviceEntryForm.$.vin.value
            ? { id: selectedVehicleDisplayName, label: serviceEntryForm.$.vin.value }
            : null;

        return (
            <Page>
                <Page.Header title={isEditMode ? 'Edit Service Entry' : 'Add Service Entry'} />
                <Page.Content>
                    <Stepper
                        defaultIndex={0}
                        allowNavigateToPrevStep
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                        }}
                        onChange={({ currentStepId }) => {
                            setStep(currentStepId ?? 'service-details');
                        }}
                    >
                        <Flex
                            direction="column"
                            className="a2-p-block-6 a2-p-inline-5"
                            style={{
                                flex: 1,
                                overflowY: 'auto',
                                minHeight: 0,
                            }}
                        >
                            <Stepper.List className="a2-m-block-end-8">
                                <Stepper.Step id="service-details" controls="service-details">
                                    Service details
                                </Stepper.Step>
                                <Stepper.Step id="services" controls="services">
                                    Services
                                </Stepper.Step>
                                <Stepper.Step id="additional-details" controls="additional-details">
                                    Additional Details (optional)
                                </Stepper.Step>
                            </Stepper.List>

                            {/* Step 1: Service Details */}
                            <Stepper.Panel id="service-details">
                                <Flex direction="column" gap="6">
                                    <Flex direction="column" gap="1">
                                        <Text variant="eyebrow">Vehicle</Text>
                                        <Text variant="body">
                                            <b>{selectedVehicleDisplayName}</b>
                                        </Text>
                                    </Flex>
                                    <SingleSelect
                                        items={vinOptions}
                                        label="VIN"
                                        modalTitle="Add VIN"
                                        placeholder="Enter VIN"
                                        required
                                        searchable
                                        filterLocally={false}
                                        searchPlaceholder="Search for VIN"
                                        minSearchLength={5}
                                        isLoading={
                                            fetchVehiclesByVinPrefixRequestState ===
                                            RequestState.Loading
                                        }
                                        onSearch={fetchVehiclesByVinPrefix}
                                        selectedItem={selectedVinOption}
                                        onChange={handleVinChange}
                                        error={serviceEntryForm.$.vin.error ?? undefined}
                                        emptyStateIcon={<NotFoundIllustration />}
                                        emptyStateTitle="VIN Not Found"
                                        emptyStateDescription="We couldn’t find a vehicle matching this VIN. Please check the number and try again."
                                    />
                                    <SingleSelect
                                        items={categoryOptions}
                                        label="Category"
                                        placeholder="Select Category"
                                        selectedItem={serviceEntryForm.$.category.value}
                                        onChange={value => {
                                            serviceEntryForm.$.category.onChange(value);
                                        }}
                                        error={serviceEntryForm.$.category.error ?? undefined}
                                        required
                                    />
                                    <DateField
                                        // @ts-expect-error @servicetitan/web-feature-ui DateField's label is typed as a string but according to the source code it can take
                                        // a ReactElement as well and since it doesn't have a required prop so I'm suppressing this TS error and passing an element
                                        label={
                                            <Text
                                                variant="body"
                                                size="small"
                                                className={styles.serviceDateFieldLabel}
                                            >
                                                <b>
                                                    Service Date{' '}
                                                    <span className="a2-c-danger">*</span>
                                                </b>
                                            </Text>
                                        }
                                        className={styles.serviceDateField}
                                        max={formatDate(new Date())}
                                        value={formatDate(serviceEntryForm.$.serviceDate.value)}
                                        onChange={e => {
                                            if (e?.detail?.value) {
                                                const date = new Date(e.detail.value);
                                                serviceEntryForm.$.serviceDate.onChange(date);
                                            }
                                        }}
                                        error={!!serviceEntryForm.$.serviceDate.error}
                                    />
                                </Flex>
                            </Stepper.Panel>

                            {/* Step 2: Services */}
                            <Stepper.Panel id="services">
                                <Flex direction="column" gap="4">
                                    {serviceEntryForm.$.serviceRecords.$.map((record, index) => {
                                        const serviceRecordCard = (
                                            <Card
                                                key={record.$.id.value ?? index}
                                                flexDirection="column"
                                                gap="4"
                                            >
                                                <SingleSelect
                                                    items={serviceTypeOptions}
                                                    initialBreakpoint={1}
                                                    label="Service Type"
                                                    placeholder="Select Service Type"
                                                    required
                                                    searchable
                                                    selectedItem={record.$.serviceType.value}
                                                    onChange={value => {
                                                        record.$.serviceType.onChange(value);
                                                    }}
                                                    error={record.$.serviceType.error ?? undefined}
                                                />
                                                <NumberField
                                                    label="Cost"
                                                    placeholder="$0"
                                                    size="large"
                                                    maximumFractionDigits={2}
                                                    hideControls
                                                    value={record.$.cost.value}
                                                    onChange={value => {
                                                        record.$.cost.onChange(value ?? 0);
                                                    }}
                                                />
                                                <Button
                                                    icon={IconDelete}
                                                    aria-label="Delete service"
                                                    disabled={
                                                        serviceEntryForm.$.serviceRecords.$
                                                            .length === 1
                                                    }
                                                    alignSelf="flex-end"
                                                    onClick={() => removeServiceRecord(index)}
                                                />
                                            </Card>
                                        );

                                        // Show Add Service button after first record
                                        if (index === 0) {
                                            return (
                                                <Fragment key={record.$.id.value ?? index}>
                                                    {serviceRecordCard}
                                                    <Button
                                                        appearance="secondary"
                                                        icon={IconAdd}
                                                        onClick={addServiceRecord}
                                                        style={{
                                                            width: '100%',
                                                        }}
                                                    >
                                                        Add Service
                                                    </Button>
                                                </Fragment>
                                            );
                                        }

                                        return serviceRecordCard;
                                    })}
                                </Flex>
                            </Stepper.Panel>

                            {/* Step 3: Additional Details */}
                            <Stepper.Panel id="additional-details">
                                <Text variant="body" className="a2-m-block-end-6">
                                    Add more details to complete the record.
                                </Text>
                                <Flex direction="column" gap="4">
                                    <NumberField
                                        label="Odometer"
                                        placeholder="0"
                                        size="large"
                                        hideControls
                                        prefix="Mi"
                                        value={serviceEntryForm.$.odometerInMiles.value}
                                        onChange={value => {
                                            serviceEntryForm.$.odometerInMiles.onChange(value ?? 0);
                                        }}
                                    />
                                    <NumberField
                                        label="Engine Hours"
                                        placeholder="0"
                                        size="large"
                                        hideControls
                                        prefix="Hrs"
                                        value={serviceEntryForm.$.engineRunTimeInHours.value}
                                        onChange={value => {
                                            serviceEntryForm.$.engineRunTimeInHours.onChange(
                                                value ?? 0
                                            );
                                        }}
                                    />
                                    <TextField
                                        label="Service Center"
                                        placeholder="Enter Service Center"
                                        size="large"
                                        value={serviceEntryForm.$.serviceCenter.value ?? ''}
                                        onChange={e => {
                                            serviceEntryForm.$.serviceCenter.onChange(
                                                e.target.value || undefined
                                            );
                                        }}
                                    />
                                    <TextField
                                        label="Invoice Number"
                                        placeholder="Enter Invoice Number"
                                        size="large"
                                        value={serviceEntryForm.$.invoiceNumber.value ?? ''}
                                        onChange={e => {
                                            serviceEntryForm.$.invoiceNumber.onChange(
                                                e.target.value || undefined
                                            );
                                        }}
                                    />
                                    <Textarea
                                        label="Notes"
                                        placeholder="Add notes here"
                                        maxLength={256}
                                        showCounter
                                        rows={6}
                                        value={serviceEntryForm.$.notes.value ?? ''}
                                        onChange={e => {
                                            if (e?.target) {
                                                serviceEntryForm.$.notes.onChange(
                                                    e.target.value || undefined
                                                );
                                            }
                                        }}
                                        error={serviceEntryForm.$.notes.error ?? undefined}
                                    />

                                    {/* File Attachments */}
                                    <Flex direction="column" gap="2">
                                        <Text variant="eyebrow">Attachments</Text>
                                        <Button
                                            appearance="secondary"
                                            icon={IconAttachFile}
                                            onClick={handleAddFileClick}
                                        >
                                            Add Files
                                        </Button>
                                        {hasFileValidationError && (
                                            <Text
                                                variant="body"
                                                size="small"
                                                className="a2-c-danger"
                                            >
                                                Please upload up to 5 files, each no more than 5MB.
                                            </Text>
                                        )}
                                        {serviceEntryForm.$.fileAttachments.value.length > 0 && (
                                            <Flex
                                                gap="2"
                                                wrap="wrap"
                                                direction="column"
                                                className="a2-m-block-start-2"
                                            >
                                                {serviceEntryForm.$.fileAttachments.value.map(
                                                    (file, index) => (
                                                        <Flex
                                                            key={index}
                                                            alignItems="center"
                                                            justifyContent="space-between"
                                                            gap="1"
                                                        >
                                                            <Chip label={getFileName(file)} />
                                                            <Button
                                                                size="small"
                                                                icon={IconClose}
                                                                aria-label="Remove file"
                                                                onClick={() =>
                                                                    handleFileDelete(file)
                                                                }
                                                            />
                                                        </Flex>
                                                    )
                                                )}
                                            </Flex>
                                        )}
                                        <Text variant="body" size="small" subdued>
                                            You can upload up to 5 files, each 5MB or smaller.
                                        </Text>
                                    </Flex>
                                </Flex>
                            </Stepper.Panel>
                        </Flex>
                        <StickyFooter
                            style={{
                                alignSelf: 'flex-end',
                            }}
                        >
                            <Flex direction="column" gap="3" style={{ width: '100%' }}>
                                <Stepper.NextButton
                                    disabled={
                                        isSubmitting || !serviceEntryForm || !isStepValid(step)
                                    }
                                    onClick={() => {
                                        if (step === 'additional-details') {
                                            handleComplete();
                                            return false;
                                        }
                                    }}
                                    nextLabel="Next"
                                    completeLabel={
                                        isEditMode ? 'Update Service Entry' : 'Add Service Entry'
                                    }
                                    style={{
                                        width: '100%',
                                    }}
                                />
                                <Stepper.PrevButton
                                    style={{
                                        width: '100%',
                                    }}
                                />
                            </Flex>
                        </StickyFooter>
                    </Stepper>
                </Page.Content>
            </Page>
        );
    })
);
